import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook that polls a job status endpoint at a fixed interval.
 *
 * @param {Function} pollFn  - Async function that returns { status, progress, error, ...rest }.
 * @param {boolean}  enabled - Whether polling is active (default false).
 * @param {number}   intervalMs - Polling interval in milliseconds (default 2000).
 * @param {number}   maxDurationMs - Maximum polling duration before auto-timeout (default: 2 hours).
 * @returns {{ status: string|null, progress: number|null, error: string|null, data: object|null }}
 */
export function useJobPolling(pollFn, enabled = false, intervalMs = 2000, maxDurationMs = 2 * 60 * 60 * 1000) {
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Keep a stable reference to the latest pollFn so the interval
  // always calls the most recent version without restarting the timer.
  const pollFnRef = useRef(pollFn);
  useEffect(() => {
    pollFnRef.current = pollFn;
  }, [pollFn]);

  // Track current status in a ref so the interval can read it without stale closures
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Reset state when polling starts fresh
    setStatus(null);
    setProgress(null);
    setError(null);
    setData(null);

    let cancelled = false;
    const startTime = Date.now();

    const poll = async () => {
      // Auto-timeout if polling has exceeded the maximum duration
      if (Date.now() - startTime > maxDurationMs) {
        setError('Polling timed out — job may still be running. Refresh to check status.');
        setStatus('failed');
        return;
      }
      try {
        const result = await pollFnRef.current();
        if (cancelled) return;

        const { status: s, progress: p, error: e, ...rest } = result;
        setStatus(s);
        setProgress(p ?? null);
        setError(e ?? null);
        setData(Object.keys(rest).length > 0 ? rest : null);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Polling failed');
        setStatus('failed');
      }
    };

    // Fire an initial poll immediately, then start the interval.
    poll();

    const id = setInterval(() => {
      const cur = statusRef.current;
      if (cur === 'complete' || cur === 'failed' || cur === 'idle') {
        clearInterval(id);
      } else {
        poll();
      }
    }, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, intervalMs, maxDurationMs]);

  return { status, progress, error, data };
}

export default useJobPolling;
